import { Card, Checkbox, IconButton, Input, Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, File, Folder, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type GoogleDriveItem, listFilesOptions, searchFilesOptions } from "@/api";
import { Text } from "@/components/Text";

/** Browse or search the user's Google Drive and pick files (folders are for browsing). */
export function GoogleDrivePicker({ onFiles }: Readonly<{ onFiles: (files: GoogleDriveItem[]) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const [folders, setFolders] = useState<GoogleDriveItem[]>([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<GoogleDriveItem[]>([]);

  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  const folder = folders.at(-1);
  const listing = useQuery({
    ...listFilesOptions({ query: folder ? { folder_id: folder.id } : {} }),
    enabled: !query,
    select: (r) => r.items ?? [],
  });
  const results = useQuery({ ...searchFilesOptions({ query: { q: query } }), enabled: Boolean(query) });
  const items = (query ? results.data : listing.data) ?? [];
  const loading = query ? results.isPending : listing.isPending;

  const toggle = (item: GoogleDriveItem, on: boolean) => {
    const next = on ? [...selected, item] : selected.filter((f) => f.id !== item.id);
    setSelected(next);
    onFiles(next);
  };

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <Input
          leadingIcon={<Search />}
          placeholder={t("searchDrive")}
          aria-label={t("searchDrive")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!query && folder && (
          <div className="flex items-center gap-2">
            <IconButton
              icon={<ArrowLeft />}
              aria-label={t("up")}
              variant="ghost"
              size="sm"
              onClick={() => setFolders((s) => s.slice(0, -1))}
            />
            <Text variant="body-sm" tone="secondary">
              {folder.name}
            </Text>
          </div>
        )}
        {loading && <Spinner size="md" aria-label={t("loading")} />}
        {!loading && items.length === 0 && (
          <Text variant="body-sm" tone="tertiary">
            {query ? t("noResults") : t("emptyFolder")}
          </Text>
        )}
        {!loading && items.length > 0 && (
          <ul className="flex max-h-72 flex-col divide-y divide-border-subtle overflow-y-auto">
            {items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 py-2">
                {item.is_folder ? (
                  <span className="w-4 shrink-0" />
                ) : (
                  <Checkbox
                    aria-label={item.name}
                    checked={selected.some((f) => f.id === item.id)}
                    onChange={(on) => toggle(item, on)}
                  />
                )}
                <button
                  type="button"
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left disabled:cursor-default"
                  disabled={!item.is_folder}
                  onClick={() => {
                    setSearch("");
                    setFolders((s) => [...s, item]);
                  }}
                >
                  <span className="text-content-tertiary">
                    {item.is_folder ? <Folder size={16} /> : <File size={16} />}
                  </span>
                  <Text variant="body-sm" className="truncate">
                    {item.name}
                  </Text>
                  {item.is_folder && <ChevronRight size={14} className="text-content-tertiary" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
